import { type ReactNode, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { buscarIndicacaoPublica, buscarConfiguracaoPublica } from '../lib/api';
import { linkWhatsApp } from '../lib/basePath';
import { ehPlaceholder, valorConfig } from '../lib/formato';
import { Wordmark } from '../componentes/Wordmark';
import { AvisoCampanha } from '../componentes/AvisoCampanha';
import { Botao, Esqueleto } from '../componentes/ui';
import { GlifoWhatsApp, IconeLocal, IconeRelogio } from '../componentes/icones';

/**
 * Landing do indicado (UX §6, DESIGN-FOUNDATION §6.7) — rota pública
 * `/conexao-varga/convite?codigo=`, **sem shell de app, sem auth, sem formulário,
 * sem pixel de rastreio**. É a peça que protege o indicador do risco social.
 *
 * Placa **vazada** (é código de outra pessoa — "anote"). Nome do indicador
 * **não** colorido de vermelho (vermelho é ação). Código inexistente/inativo →
 * versão sem personalização ("Bem-vindo à Varga Car Service"), **nunca** revela
 * que o código não existe.
 */
export function LandingConvite(): ReactNode {
  const [params] = useSearchParams();
  const codigo = (params.get('codigo') ?? '').trim();

  const indicacao = useQuery({
    queryKey: ['publico', 'indicacao', codigo || '-'],
    queryFn: () => buscarIndicacaoPublica(codigo || '-'),
  });
  const config = useQuery({
    queryKey: ['publico', 'configuracao'],
    queryFn: buscarConfiguracaoPublica,
    staleTime: 5 * 60_000,
  });

  const campanhaFim = config.data ? valorConfig(config.data, 'campanha_fim') : null;

  return (
    <div className="min-h-screen bg-fundo px-5 py-10">
      <div className="mx-auto w-full max-w-[560px]">
        <div className="mb-8 flex justify-center">
          <Wordmark largura={140} />
        </div>

        {indicacao.isLoading ? (
          <div className="flex flex-col gap-4">
            <Esqueleto className="h-8 w-3/4 rounded-1" />
            <Esqueleto className="h-20 rounded-2" />
            <Esqueleto className="h-14 rounded-1" />
          </div>
        ) : (
          <Conteudo
            personalizado={
              indicacao.data?.indicadorPrimeiroNome
                ? {
                    nome: indicacao.data.indicadorPrimeiroNome,
                    codigo,
                    brinde: indicacao.data.brindeIndicadoDescricao,
                  }
                : null
            }
            loja={indicacao.data?.loja ?? null}
            campanhaFim={campanhaFim}
          />
        )}
      </div>
    </div>
  );
}

type Loja = {
  nome: string;
  bairro: string | null;
  endereco: string | null;
  horario: string | null;
  whatsapp: string | null;
  descricaoCurta: string | null;
};

function Conteudo({
  personalizado,
  loja,
  campanhaFim,
}: {
  personalizado: { nome: string; codigo: string; brinde: string | null } | null;
  loja: Loja | null;
  campanhaFim: string | null;
}): ReactNode {
  const nomeLoja = loja?.nome ?? 'Varga Car Service';
  const util = (v: string | null | undefined): string | null =>
    ehPlaceholder(v) ? null : (v ?? null);
  const bairro = util(loja?.bairro);
  const endereco = util(loja?.endereco);
  const horario = util(loja?.horario);
  const descricao = util(loja?.descricaoCurta);
  const wa = linkWhatsApp(util(loja?.whatsapp));

  return (
    <div className="flex flex-col gap-5">
      <h1 className="tipo-display text-tinta-forte">
        {personalizado
          ? `${personalizado.nome} indicou a ${nomeLoja} pra você.`
          : `Bem-vindo à ${nomeLoja}.`}
      </h1>

      <p className="tipo-corpo text-tinta">
        Somos um centro automotivo{bairro ? ` em ${bairro}` : ''}.{descricao ? ` ${descricao}` : ''}
      </p>

      {personalizado && (
        <div className="flex flex-col gap-3">
          <p className="tipo-corpo text-tinta">
            Faça um serviço e apresente este código — você ganha{' '}
            <span className="tipo-corpo-forte text-tinta-forte">
              {personalizado.brinde ?? 'um brinde de boas-vindas'}
            </span>
            .
          </p>
          <PlacaVazada codigo={personalizado.codigo} />
        </div>
      )}

      {(endereco || horario) && (
        <dl className="divide-y divide-linha border-y border-linha">
          {endereco && (
            <ParLojaLinha
              icone={<IconeLocal width={18} height={18} />}
              rotulo="Endereço"
              valor={endereco}
            />
          )}
          {horario && (
            <ParLojaLinha
              icone={<IconeRelogio width={18} height={18} />}
              rotulo="Horário"
              valor={horario}
            />
          )}
        </dl>
      )}

      {wa && (
        <Botao variante="primario" onClick={() => window.open(wa, '_blank', 'noopener')}>
          <GlifoWhatsApp />
          Falar com a Varga no WhatsApp
        </Botao>
      )}

      <AvisoCampanha campanhaFim={campanhaFim} />

      <p className="mt-4 border-t border-linha pt-5 tipo-nota text-tinta-3">
        Programa válido até 07/07/2027.
      </p>
    </div>
  );
}

function ParLojaLinha({
  icone,
  rotulo,
  valor,
}: {
  icone: ReactNode;
  rotulo: string;
  valor: string;
}): ReactNode {
  return (
    <div className="flex items-start gap-3 py-3">
      <span className="mt-0.5 text-tinta-2">{icone}</span>
      <div className="flex flex-col gap-0.5">
        <dt className="tipo-rotulo text-tinta-2">{rotulo}</dt>
        <dd className="tipo-corpo text-tinta-forte">{valor}</dd>
      </div>
    </div>
  );
}

/**
 * Placa vazada (DESIGN-FOUNDATION §5.2) — `--poco`, letras `--tinta-forte`, borda
 * `--linha-forte`. "É de outra pessoa, anote." Copiável.
 */
function PlacaVazada({ codigo }: { codigo: string }): ReactNode {
  const [copiado, setCopiado] = useState(false);
  const copiar = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(codigo);
      setCopiado(true);
      window.setTimeout(() => setCopiado(false), 1600);
    } catch {
      /* clipboard indisponível — o código continua visível para anotar */
    }
  };
  return (
    <button
      type="button"
      onClick={() => void copiar()}
      aria-label={`Copiar o código ${codigo}`}
      className="flex h-20 w-full items-center justify-center gap-3 rounded-3 border border-linha-forte bg-poco tipo-placa-sm text-tinta-forte"
    >
      {codigo}
      <span aria-live="polite" className="tipo-rotulo-xs text-tinta-2">
        {copiado ? 'copiado' : 'toque para copiar'}
      </span>
    </button>
  );
}
